<template>
  <div>
    <!-- Filters -->
    <form>
      <div class="p-fluid p-formgrid p-grid">
        <div class="p-field p-col-12 p-md-4">
          <label for="monthSelect">Select Month *</label>
          <Dropdown
            id="monthSelect"
            v-model="selectedMonth"
            :options="nepaliMonths"
            optionLabel="name"
            optionValue="name"
            placeholder="Select Month"
            @change="searchReport"
            :class="{ 'p-invalid': !selectedMonth && submitted }"
          />
          <small v-if="!selectedMonth && submitted" class="p-error">Month is required.</small>
        </div>

        <div class="p-field p-col-12 p-md-6">
          <label for="userSelect">Select Users (Optional)</label>
          <MultiSelect
            v-model="selectedUsers"
            :options="userOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="All Users"
            :showCheckbox="true"
            display="chip"
            class="multiselect-custom"
            :filter="true"
            :loading="loadingUsers"
          >
            <template #header>
              <div class="p-py-2 p-px-3">
                <Checkbox v-model="allSelected" :binary="true" @change="toggleSelectAll" />
                <span class="p-ml-2">Select All</span>
              </div>
            </template>
            <template #option="slotProps">
              <div>{{ slotProps.option.name }} <small class="p-text-muted">({{ slotProps.option.username }})</small></div>
            </template>
          </MultiSelect>
        </div>

        <div class="p-field p-col-12 p-md-2 p-mt-5">
          <Button
            @click.prevent="searchReport"
            class="p-button-raised p-button-primary"
            label="Search"
            icon="pi pi-search"
            :loading="loading"
          />
        </div>
      </div>
    </form>

    <div class="chart-container p-grid p-mt-5">
      <div class="p-col-12 p-md-6 chart-wrapper">
        <h4>Weekly Follow-up Count (Bar)</h4>
        <div class="chart-subcontainer">
          <div v-if="loading" class="chart-loading"><i class="pi pi-spin pi-spinner"></i> Loading...</div>
          <div v-else-if="!hasChartData" class="chart-empty">No data</div>
          <canvas ref="barChart" :style="{ display: loading || !hasChartData ? 'none' : 'block' }"></canvas>
        </div>
      </div>
      <div class="p-col-12 p-md-6 chart-wrapper">
        <h4>Total Follow-ups per User (Pie)</h4>
        <div class="chart-subcontainer">
          <div v-if="loading" class="chart-loading"><i class="pi pi-spin pi-spinner"></i> Loading...</div>
          <div v-else-if="!hasChartData" class="chart-empty">No data</div>
          <canvas ref="pieChart" :style="{ display: loading || !hasChartData ? 'none' : 'block' }"></canvas>
        </div>
      </div>
      <div class="p-col-12 chart-wrapper">
        <h4>Follow-up Trend by User (Line)</h4>
        <div class="chart-subcontainer">
          <div v-if="loading" class="chart-loading"><i class="pi pi-spin pi-spinner"></i> Loading...</div>
          <div v-else-if="!hasChartData" class="chart-empty">No data</div>
          <canvas ref="lineChart" :style="{ display: loading || !hasChartData ? 'none' : 'block' }"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'chart.js'
import Dropdown from 'primevue/dropdown'
import MultiSelect from 'primevue/multiselect'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'

export default {
  name: 'CRMUserWiseMonthlyReport',
  components: {
    Dropdown,
    MultiSelect,
    Button,
    Checkbox
  },
  data() {
    return {
      selectedMonth: null,
      selectedUsers: [],
      userOptions: [],
      allSelected: false,
      nepaliMonths: [
        { name: 'Baisakh' }, { name: 'Jestha' }, { name: 'Ashadh' },
        { name: 'Shrawan' }, { name: 'Bhadra' }, { name: 'Ashwin' },
        { name: 'Kartik' }, { name: 'Mangsir' }, { name: 'Poush' },
        { name: 'Magh' }, { name: 'Falgun' }, { name: 'Chaitra' }
      ],
      loadingUsers: false,
      loading: false,
      hasChartData: false,
      barChart: null,
      pieChart: null,
      lineChart: null,
      submitted: false
    }
  },
  computed: {
    allUserIds() {
      return this.userOptions.map(u => u.id)
    }
  },
  watch: {
    selectedUsers(newVal) {
      this.allSelected = newVal.length === this.userOptions.length
    }
  },
  async mounted() {
    await this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loadingUsers = true
      try {
        const response = await this.$store.dispatch('loadUser')
        if (response.data.data) {
          this.userOptions = response.data.data
            .filter(user => user)
            .map(user => ({
              id: user.id,
              name: user.name || user.username || 'Unknown User',
              username: user.username
            }))
        }
      } catch (error) {
        console.error('Failed to load users:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not load user list',
          life: 3000
        })
      } finally {
        this.loadingUsers = false
      }
    },
    toggleSelectAll() {
      this.allSelected = !this.allSelected
      this.selectedUsers = this.allSelected ? this.allUserIds : []
    },
    async searchReport() {
      this.submitted = true
      if (!this.selectedMonth) return
      this.loading = true
      this.hasChartData = false
      this.cleanupCharts()
      try {
        const payload = {
          month: this.selectedMonth,
          users: this.selectedUsers.length > 0 ? this.selectedUsers : null
        }
        const response = await this.$store.dispatch('loadCRMUserWiseMonthlyGraphData', payload)
        this.hasChartData = true
        this.createBarChart(response)
        this.createPieChart(response)
        this.createLineChart(response)
      } catch (error) {
        console.error('Error loading CRM monthly user data:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load report data',
          life: 3000
        })
      } finally {
        this.loading = false
      }
    },
    createBarChart(data) {
      const ctx = this.$refs.barChart.getContext('2d')
      this.barChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.labels,
          datasets: data.datasets.map(ds => ({
            label: ds.label,
            data: ds.data,
            backgroundColor: ds.backgroundColor,
            borderColor: ds.borderColor,
            borderWidth: 1
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Follow-up Count by User (Weekly) - ${this.selectedMonth}`
            },
            legend: {
              position: 'top'
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Weeks'
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Follow-up Count'
              },
              ticks: { precision: 0 }
            }
          }
        }
      })
    },
    createPieChart(data) {
      const userTotals = {}
      data.datasets.forEach(ds => {
        const total = ds.data.reduce((sum, val) => sum + (val || 0), 0)
        if (total > 0) {
          userTotals[ds.label] = total
        }
      })
      const labels = Object.keys(userTotals)
      const values = Object.values(userTotals)
      const colors = data.datasets
        .filter(ds => userTotals[ds.label])
        .map(ds => Array.isArray(ds.backgroundColor) ? ds.backgroundColor[0] : ds.backgroundColor)
      const ctx = this.$refs.pieChart.getContext('2d')
      this.pieChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: colors.length > 0 ? colors : [
              '#42b3f4', '#dc3545', '#fd7e14', '#28a745', '#6f42c1'
            ],
            hoverOffset: 10
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Total Follow-ups by User - ${this.selectedMonth}`
            },
            legend: { position: 'right' }
          }
        }
      })
    },
    createLineChart(data) {
      const canvas = this.$refs.lineChart
      if (!canvas) return
      if (this.lineChart) {
        this.lineChart.destroy()
      }
      const ctx = canvas.getContext('2d')
      this.lineChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: data.datasets.map(ds => ({
            label: ds.label,
            data: ds.data,
            backgroundColor: this.hexToRgba(
              Array.isArray(ds.backgroundColor) ? ds.backgroundColor[0] : ds.backgroundColor,
              0.2
            ),
            borderColor: Array.isArray(ds.borderColor)
              ? ds.borderColor[0]
              : ds.borderColor || ds.backgroundColor[0] || '#36A2EB',
            borderWidth: 3,
            pointBackgroundColor: Array.isArray(ds.backgroundColor)
              ? ds.backgroundColor[0]
              : ds.backgroundColor,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6,
            fill: false,
            tension: 0.4
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true,
                boxWidth: 10,
                padding: 15
              }
            },
            title: {
              display: true,
              text: `Follow-up Trend by User - ${this.selectedMonth}`
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { precision: 0 },
              title: { display: true, text: 'Follow-up Count' }
            },
            x: {
              title: { display: true, text: 'Weeks' }
            }
          }
        }
      })
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    cleanupCharts() {
      if (this.barChart) {
        this.barChart.destroy()
        this.barChart = null
      }
      if (this.pieChart) {
        this.pieChart.destroy()
        this.pieChart = null
      }
      if (this.lineChart) {
        this.lineChart.destroy()
        this.lineChart = null
      }
    }
  },
  beforeDestroy() {
    this.cleanupCharts()
  }
}
</script>

<style scoped>
/* Same styles */
.multiselect-custom {
  font-size: 1.2rem;
  min-height: 40px;
}

::v-deep .p-multiselect-label {
  padding: 0.75rem !important;
  font-size: 1.2rem !important;
  min-height: 40px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chart-container {
  margin-top: 2rem;
}

.chart-wrapper {
  padding: 1rem;
}

.chart-subcontainer {
  height: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1rem;
  position: relative;
}

.chart-loading, .chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1.2rem;
  color: #6c757d;
}

.chart-loading i {
  margin-right: 8px;
  font-size: 1.5rem;
}

h4 {
  color: #444;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
  font-size: 1.4rem;
}

::v-deep .p-multiselect-header {
  padding: 0.5rem !important;
  border-bottom: 1px solid #dee2e6;
}

.p-invalid {
  border: 1px solid #f44336 !important;
}
</style>



// WEBPACK FOOTER //
// crmUserWiseMonthlyGraph.vue?15889726